// src/services/entryMapper.ts
//
// Single source of truth for turning a raw backend entry JSON object into an
// EntryModel. This exact object shape was previously copy-pasted into
// clubEntryService, customTopicEntryService, personEntryService, entryService,
// followService and profileService — six near-identical mappers that drifted
// apart over time (some had the editedAt fallback, some did not; only the
// profile mapper carried the custom-topic fields). Centralizing it keeps every
// list — match, club, team, person, feed, profile — consistent.

import { EntryModel } from "../models";
import { SportTypeEnum } from "../models/enums/SportTypeEnum";
import { VoteTypeEnum } from "../models/enums/VoteTypeEnum";
import { parseServerDate } from "../utils/dateUtils";

/**
 * Maps one raw backend entry object to an EntryModel.
 *
 * Backend sends LocalDateTime strings without a timezone; parseServerDate
 * appends the Z. When editedAt is missing we fall back to createdAt so the UI
 * never has to render an Invalid Date.
 */
export const mapRawEntry = (raw: any): EntryModel => ({
  id:                raw.id,
  content:           raw.content,
  createdAt:         parseServerDate(raw.createdAt),
  editedAt:          raw.editedAt ? parseServerDate(raw.editedAt) : parseServerDate(raw.createdAt),
  userId:            raw.userId,
  username:          raw.username,
  authorRole:        raw.authorRole || "ROOKIE",
  matchId:           raw.matchId ?? null,
  homeTeam:          raw.homeTeam ?? "",
  awayTeam:          raw.awayTeam ?? "",
  matchDate:         raw.matchDate ? new Date(raw.matchDate) : new Date(),
  sportType:         (raw.sportType as SportTypeEnum) || SportTypeEnum.FOOTBALL,
  plusCount:         raw.plusCount  || 0,
  minusCount:        raw.minusCount || 0,
  favCount:          raw.favCount   || 0,
  currentUserRating: raw.currentUserRating as VoteTypeEnum | null ?? null,
  currentUserHasFav: raw.currentUserHasFav || false,
  personId:          raw.personId       ?? null,
  personFullName:    raw.personFullName  ?? null,
  personSlug:        raw.personSlug      ?? null,
  clubId:            raw.clubId          ?? null,
  clubName:          raw.clubName        ?? null,
  clubSlug:          raw.clubSlug        ?? null,
  teamId:            raw.teamId          ?? null,
  teamName:          raw.teamName        ?? null,
  teamSlug:          raw.teamSlug        ?? null,
  customTopicId:     raw.customTopicId    ?? null,
  customTopicTitle:  raw.customTopicTitle ?? null,
  customTopicSlug:   raw.customTopicSlug  ?? null,
});
